import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import useTranslation from '../hooks/useTranslation';
import { useAddress } from '../contexts/AddressContext';
import { getRecentOrders } from '../api/orderService';
import { RecentAddressOrder } from '../types/order';
import { getCurrentServiceUserId } from '../services/OperationTimeService';
import {
  formatOrderTimeOnly,
  getLocalizedOrderText,
  getOrderStatusDisplay,
  getOrderStatusStep,
  getOrderTimeLabelKey,
  summarizeOrderItems,
} from '../utils/orderDisplayUtils';

const STATUS_STEPS = [
  'PAID',
  'BROKER_ACCEPTED',
  'COOKING',
  'DELIVERY_COMPLETED',
];

const statusToneClasses = {
  info: 'bg-blue-50 text-blue-700 border-blue-100',
  success: 'bg-green-50 text-green-700 border-green-100',
  warning: 'bg-amber-50 text-amber-700 border-amber-100',
  danger: 'bg-red-50 text-red-700 border-red-100',
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { address } = useAddress();
  const [orders, setOrders] = useState<RecentAddressOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stayId = address?.stay?.stay_id?.trim();
  const serviceUserId = getCurrentServiceUserId()?.trim();
  const canLoadOrders = !!(stayId || serviceUserId);
  const currentAddress = [address?.mainAddress, address?.detailAddress].filter(Boolean).join(' ');

  const loadOrders = useCallback(async () => {
    if (!stayId && !serviceUserId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getRecentOrders({ stayId, serviceUserId });
      setOrders(response.orders);
    } catch (loadError) {
      console.error('[OrdersPage] Failed to load recent orders:', loadError);
      setError('orders.errorDescription');
    } finally {
      setIsLoading(false);
    }
  }, [stayId, serviceUserId]);

  useEffect(() => {
    if (!canLoadOrders) {
      setOrders([]);
      setError(null);
      return;
    }

    loadOrders();
  }, [canLoadOrders, loadOrders]);

  const renderProgress = (order: RecentAddressOrder) => {
    const activeStep = getOrderStatusStep(order.status);

    if (activeStep === null || order.status === 'DELIVERY_COMPLETED') {
      return null;
    }

    return (
      <div className="mt-3" aria-label={t('orders.progressLabel')}>
        <div className="flex items-center">
          {STATUS_STEPS.map((stepStatus, index) => (
            <React.Fragment key={stepStatus}>
              {index > 0 && (
                <div className="flex-1 h-0.5 mx-2 bg-gray-200 rounded-full">
                  <div
                    className={`h-full rounded-full ${index <= activeStep ? 'bg-green-500' : 'bg-gray-200'}`}
                  />
                </div>
              )}
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${
                  index <= activeStep ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                }`}
              >
                {index < activeStep ? '✓' : index + 1}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderOrderCard = (order: RecentAddressOrder) => {
    const status = getOrderStatusDisplay(order.status);
    const itemSummary = summarizeOrderItems(order.items, 2, language);
    const orderDisplayName = getLocalizedOrderText(
      order.franchiseName || order.restaurantName || '',
      order.franchiseNameI18n || order.restaurantNameI18n,
      language,
    );

    return (
      <div
        key={order.orderId}
        className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 text-left">
            <h2 className="text-lg font-bold text-gray-900 truncate text-left">
              {orderDisplayName}
            </h2>
            <p className="text-xs text-gray-500 mt-1 text-left">
              {t('orders.paidAt')} {formatOrderTimeOnly(order.paidAt, language)}
            </p>
          </div>
          <span
            className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${statusToneClasses[status.tone]}`}
          >
            {t(status.labelKey)}
          </span>
        </div>

        {order.status === 'CANCELLED' && (
          <p className="text-sm text-gray-600 mt-3 text-left">{t(status.descriptionKey)}</p>
        )}

        {order.expectedDeliveryCompletedAt && (
          <p className="text-sm text-gray-700 mt-2 font-medium text-left">
            {t(getOrderTimeLabelKey(order.status))} {formatOrderTimeOnly(order.expectedDeliveryCompletedAt, language)}
          </p>
        )}

        {renderProgress(order)}

        <div className="mt-4 pt-4 border-t border-gray-100 text-left">
          <div className="space-y-1.5 min-w-0">
            {itemSummary.visibleItems.map((item) => (
              <p key={item} className="text-sm font-medium text-gray-800 truncate">
                {item}
              </p>
            ))}
            {itemSummary.hiddenItemCount > 0 && (
              <p className="text-sm text-gray-500">
                {t('orders.hiddenItems', { count: itemSummary.hiddenItemCount })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!canLoadOrders) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">{t('orders.addressRequiredTitle')}</h2>
          <p className="text-sm text-gray-600 mt-2">{t('orders.addressRequiredDescription')}</p>
          <button
            type="button"
            onClick={() => navigate('/address')}
            className="mt-5 w-full py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
          >
            {t('orders.setAddress')}
          </button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mb-4" />
          <p className="text-sm text-gray-600">{t('orders.loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">{t('orders.errorTitle')}</h2>
          <p className="text-sm text-gray-600 mt-2">{t(error)}</p>
          <button
            type="button"
            onClick={loadOrders}
            className="mt-5 w-full py-3 border border-gray-300 text-gray-800 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            {t('orders.retry')}
          </button>
        </div>
      );
    }

    if (orders.length === 0) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 px-6 py-7 text-center shadow-sm">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-400"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M7 2h10a2 2 0 012 2v16l-3-1.5L13 20l-3-1.5L7 20V4a2 2 0 012-2zm2 5h6V5H9v2zm0 4h6V9H9v2zm0 4h4v-2H9v2z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">{t('orders.emptyTitle')}</h2>
          <p className="text-sm text-gray-600 mt-2">{t('orders.emptyDescription')}</p>
          <p className="text-xs text-gray-500 mt-2">{t('orders.emptyHint')}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-5 w-full py-3 border border-red-200 text-red-600 rounded-xl font-semibold hover:bg-red-50 transition-colors"
          >
            {t('orders.browseRestaurants')}
          </button>
          <button
            type="button"
            onClick={loadOrders}
            className="mt-3 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
          >
            {t('orders.checkAgain')}
          </button>
        </div>
      );
    }

    return <div className="space-y-3">{orders.map(renderOrderCard)}</div>;
  };

  return (
    <>
      <BackHeader title={t('orders.title')} />
      <main className="min-h-screen bg-gray-50 px-4 pt-4 pb-36">
        <section className="mb-5 text-center">
          <p className="text-sm text-gray-600">{t('orders.description')}</p>
          {currentAddress && (
            <div className="mt-3 rounded-full bg-white border border-gray-100 px-4 py-2 shadow-sm overflow-hidden">
              <p className="text-xs text-gray-500 flex items-center justify-center min-w-0">
                <span className="font-medium shrink-0">{t('orders.currentAddress')}</span>
                <span className="mx-1 text-gray-300 shrink-0">·</span>
                <span className="text-gray-700 truncate min-w-0">{currentAddress}</span>
              </p>
            </div>
          )}
        </section>

        {canLoadOrders && (orders.length > 0 || isLoading || error) && (
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">{t('orders.recentOrders')}</h2>
            <button
              type="button"
              onClick={loadOrders}
              className="text-sm font-semibold text-gray-500 hover:text-red-600 disabled:text-gray-400 transition-colors"
              disabled={isLoading}
            >
              {t('orders.refresh')}
            </button>
          </div>
        )}

        {renderContent()}
      </main>
      <NavigationBar />
    </>
  );
};

export default OrdersPage;
